import { useEffect, useRef, useState } from "react";
import KeywordFilter from "@features/mybt/components/KeywordFilter";
import useMyBtStore from "@features/mybt/store/useMyBTStore";
import { useWindowSize } from "@hooks/useWindowSize";
import { FeedTag } from "@routes/Keyword/utils/types";
import { cn } from "@util/helpers";

type MyBTOnboardKeywordListProps = {
  myBTList: FeedTag[];
};

/**
 * This component is used to display the MyBT onboard keyword list.
 */
const MyBTOnboardKeywordList = ({
  myBTList,
}: MyBTOnboardKeywordListProps): React.ReactElement => {
  const [childrenHeight, setChildrenHeight] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const childrenRef = useRef(null);
  const { isScreenLG } = useWindowSize();
  const [followedTagsStore] = useMyBtStore((store) => [store.tags]);

  useEffect(() => {
    if (childrenRef.current) {
      setChildrenHeight((childrenRef.current as HTMLDivElement).scrollHeight);
    }

    setIsExpanded(isScreenLG);
  }, [isScreenLG]);

  const toggleMoreLess = () => {
    setIsExpanded(!isExpanded);
  };

  const myBTDisplayList = isScreenLG ? myBTList : myBTList.slice(0, 7);

  return (
    <div className="md:max-w-8/10 md:justify-center lg:mx-auto lg:flex lg:flex-wrap">
      {myBTDisplayList.map((tag) => {
        const isTagFollowed = followedTagsStore.some(
          (followedTag) =>
            followedTag.name.toLowerCase() === tag.name.toLowerCase()
        );

        return (
          <MyBTOnboardKeywordItem
            key={`mybt-onboard-keyword-${tag.id}`}
            tag={tag}
            isTagFollowed={isTagFollowed}
          />
        );
      })}

      {myBTList.length > 3 && !isScreenLG ? (
        <>
          <div
            ref={childrenRef}
            className={cn(
              "overflow-hidden transition-all duration-300 lg:!h-full lg:transition-none",
              { "mt-0": !isExpanded }
            )}
            style={{
              height: isExpanded ? childrenHeight : 0,
            }}
          >
            {myBTList.slice(7).map((tag) => {
              const isTagFollowed = followedTagsStore.some(
                (followedTag) =>
                  followedTag.name.toLowerCase() === tag.name.toLowerCase()
              );

              return (
                <MyBTOnboardKeywordItem
                  key={`mybt-onboard-keyword-${tag.id}`}
                  tag={tag}
                  isTagFollowed={isTagFollowed}
                />
              );
            })}
          </div>

          {!isScreenLG ? (
            <div
              className={cn(
                "cursor-pointer font-poppins text-xs font-semibold capitalize tracking-0 !text-gray-850 [@media(hover:hover)]:hover:underline",
                { "pt-5": isExpanded }
              )}
              onClick={toggleMoreLess}
            >
              {isExpanded ? "View Less" : "View More"}
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
};

/**
 * This component is used to display a single MyBT onboard keyword item.
 */
const MyBTOnboardKeywordItem = ({
  tag,
  isTagFollowed,
}: {
  tag: FeedTag;
  isTagFollowed: boolean;
}) => {
  return (
    <KeywordFilter
      key={`mybt-onboard-keyword-${tag.id}`}
      tag={tag}
      tagClassName={cn(
        "!text-xs !font-normal border-none line-clamp-none !leading-150",
        { "text-white": isTagFollowed }
      )}
      className={cn(
        "mb-2 mr-2 inline-flex h-fit rounded-full border px-2 py-1",
        { "border-black bg-black text-white": isTagFollowed },
        { "border-gray-850 bg-white": !isTagFollowed }
      )}
      iconClassName="text-white rounded-full"
      disableTooltip
      disableBorder
      isLinkenabled={false}
      testId="mybt-keyword-button"
      buttonClassName={cn("p-1 px-2 py-1 text-white border-none", {
        "bg-black": !isTagFollowed,
      })}
    />
  );
};

export default MyBTOnboardKeywordList;
